import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TokenInterceptor } from '@app/interceptors/token.interceptor';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { NgxPermissionsModule } from 'ngx-permissions';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthComponent } from './components/auth/auth.component';
import { LeftMenuComponent } from './components/left-menu/left-menu.component';
import { LoginComponent } from './components/login/login.component';
import { LogoutComponent } from './components/logout/logout.component';
import { ActiveMenuDirective } from './components/navbar/active-menu.directive';
import { NavbarComponent } from './components/navbar/navbar.component';
import { NetworkInterceptor } from './interceptors/network.interceptor';
import { MissingTranslationService } from "./services/missingTranslation.service";
import { AdminTemplateComponent } from './templates/admin-template/admin-template.component';
import { LayoutTemplateComponent } from './templates/layout-template/layout-template.component';
import { UserTemplateComponent } from './templates/user-template/user-template.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ForgotPasswordComponent } from "@app/components/forgot-password/forgot-password.component";
import { FooterComponent } from './shared/footer/footer.component';
import { UserComponent } from "@app/components/user/user.component";
import { ToastrModule } from 'ngx-toastr';
import { ConfirmationService } from 'primeng/api';
import { AccordionModule } from "ngx-bootstrap/accordion";
import { ModalModule, BsModalService } from 'ngx-bootstrap/modal';
import { NumberFormatPipe } from "@app/shared/number.pipe";
import { DatePipe } from "@angular/common";
import { SharedModule } from './shared/shared-module';
import { TableModule } from "primeng/table";
import { MenuComponent } from './components/menu/menu.component';
import { InputSwitchModule } from 'primeng/inputswitch';
import { LanguageInterceptor } from "@app/interceptors/language.interceptor";
import { TranslateConfigService } from "@app/services/translate-config.service";
import { IndexedStorageService } from './services/indexed-storage.service';
import { IndexedTranslateLoader } from './services/indexed-translate-loader.service';
import { QuillModule } from "ngx-quill";
import { NotFoundComponent } from './components/error-pages/not-found/not-found.component';
import { ForbiddenComponent } from './components/error-pages/forbidden/forbidden.component';

export function rootLoaderFactory(indexedStorageService: IndexedStorageService) {
    return new IndexedTranslateLoader(indexedStorageService);
}

export function initApp(translateConfig: TranslateConfigService) {
    return () => translateConfig.initLanguage();
}

@NgModule({
    declarations: [
        AppComponent,
        AuthComponent,
        LoginComponent,
        ForgotPasswordComponent,
        LogoutComponent,
        NotFoundComponent,
        ForbiddenComponent,
        LayoutTemplateComponent,
        AdminTemplateComponent,
        UserTemplateComponent,
        LeftMenuComponent,
        NavbarComponent,
        ActiveMenuDirective,
        UserComponent,
        FooterComponent,
        NumberFormatPipe,
        MenuComponent
    ],
    exports: [
        NumberFormatPipe
    ],
    imports: [
        TableModule,
        BrowserModule,
        AppRoutingModule,
        HttpClientModule,
        FormsModule,
        InputSwitchModule,
        ReactiveFormsModule,
        BrowserAnimationsModule,
        MatProgressSpinnerModule,
        NgxPermissionsModule.forRoot(),
        ToastrModule.forRoot({
            preventDuplicates: true
        }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: rootLoaderFactory,
                deps: [IndexedStorageService],
            },
            missingTranslationHandler: {
                provide: MissingTranslationHandler,
                useClass: MissingTranslationService
            },
            useDefaultLang: false,
        }),
        AccordionModule.forRoot(),
        ModalModule.forRoot(),
        SharedModule,
        QuillModule.forRoot({
            theme: 'snow',
        }),
    ],
    providers: [
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: NetworkInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: LanguageInterceptor,
            multi: true
        },
        {
            provide: APP_INITIALIZER,
            useFactory: initApp,
            deps: [TranslateConfigService],
            multi: true
        },
        ConfirmationService,
        BsModalService,
        NumberFormatPipe,
        DatePipe
    ],
    bootstrap: [AppComponent]
})
export class AppModule { }
